import moment, {type Moment} from 'moment-timezone';
import {produce} from 'immer';
import i18next from 'i18next';

import {head, isNonEmpty, type NonEmptyArray} from 'web-app/util/non-empty-array';
import {indexBy} from 'web-app/util/array';
import {toPartialFunction} from 'web-app/util/record';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isEmpty} from 'web-app/util/array';
import * as GlobalEvent from 'web-app/services/global-event';

import {
    getDiscountPeriod,
    getFeePeriod,
    getTuitionPeriod,
    isWellPricedPeriod,
    makeWellPricablePlanWithCustomPayables,
    mergeWellPricedPeriods,
    type WellPricedPeriod,
} from './calculate-past-summary-helpers';
import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';
import type * as Types from '../types';
import type * as ApiTypes from '../api-types';
import {getClosestBiweeklyStartDate} from './create-billing-periods';

const selectedChildren = (
    allChildren: Array<Types.Child>,
    selectedChildIds: Array<Types.Child['id']>,
): Array<Types.Child> => allChildren.filter(child => selectedChildIds.some(childId => child.id === childId));

const allSelectedChildrenHaveBillPayer = (selectedChildren: Array<Types.Child>): boolean =>
    selectedChildren.every(child => isNonEmpty(child.billPayers));

const allSelectedTuitionsHaveTheSameFrequency = (selectedTuitions: NonEmptyArray<Types.Tuition>) =>
    selectedTuitions.every(tuition => tuition.type === selectedTuitions[0].type);

const pricingGroupSelectPlacement = (
    pricingGroups: Array<Types.InitialPricingGroup>,
    hasAutomaticPricingGroup: boolean,
): Types.PricingGroupSelectPlacement => {
    const numberOfPricingGroups = pricingGroups.length;

    if (numberOfPricingGroups === 1) {
        return 'none';
    }
    if (numberOfPricingGroups > 1 && hasAutomaticPricingGroup) {
        return 'inside-more-options';
    }
    return 'outside-more-options';
};

const selectedPricingGroup = (
    pricingGroups: Array<Types.InitialPricingGroup>,
    pricingGroupSelectPlacement: Types.State['pricingGroupState']['placement'],
) => {
    switch (pricingGroupSelectPlacement) {
        case 'inside-more-options': {
            return pricingGroups.find(isAutomaticPricingGroup)?.id ?? null;
        }
        case 'none': {
            return pricingGroups[0].id ?? null;
        }
        default:
            return null;
    }
};

const isAutomaticPricingGroup = (pricingGroup: Types.InitialPricingGroup | Types.PricingGroupOption) =>
    'id' in pricingGroup
        ? pricingGroup.id === AUTOMATIC_PRICING_GROUP_ID
        : pricingGroup.value === AUTOMATIC_PRICING_GROUP_ID;

const calculatePricingGroupTotals = (
    allPricingGroups: Array<Types.PricingGroup>,
): Types.SummaryPeriodItem['pricingGroups'] => {
    const calculatedPricingGroupTotals = allPricingGroups.reduce<Types.SummaryPeriodItem['pricingGroups']>(
        produce((acc, current) => {
            if (isEmpty(acc)) {
                return [{...current, children: null}];
            }
            const pricingGroupToChange = acc.find(pricingGroup => pricingGroup.id === current.id);
            if (!pricingGroupToChange) {
                return [...acc, {...current, children: null}];
            }

            pricingGroupToChange.price += current.price;
            return acc;
        }),
        [],
    );

    // The result of produce is typically readonly, so in order to be able to mutate the array (e.g. sort it), we must spread it into a new array
    return [...calculatedPricingGroupTotals];
};

const lowestAndHighestPrice = (prices: Array<Types.PricingGroup['price']>): Types.LowestAndHighestPrice => {
    const lowest = Math.min(...prices);
    const highest = Math.max(...prices);
    return {
        lowest: lowest === highest ? null : lowest,
        highest,
    };
};

const isEndOfPeriodDate = (
    date: Moment,
    billingFrequency: Types.BillingFrequencyOption['type'],
    biweeklyBillingStartDate: Moment,
) => {
    const selectedDate = date.clone().startOf('day');

    if (billingFrequency === 'monthly') {
        const endOfMonth = date.clone().endOf('month').startOf('day');
        return selectedDate.isSame(endOfMonth);
    }

    if (billingFrequency === 'biweekly') {
        const closestBiweeklyPeriodStartDate = getClosestBiweeklyStartDate(biweeklyBillingStartDate, date);

        const isInSameWeek = date.isSame(closestBiweeklyPeriodStartDate, 'isoWeek');

        const endOfPeriod = isInSameWeek
            ? closestBiweeklyPeriodStartDate.clone().add(1, 'week').endOf('isoWeek')
            : closestBiweeklyPeriodStartDate.clone().subtract(1, 'week').endOf('isoWeek');

        // For biweekly periods, treat Friday, Saturday and Sunday of the second week as end of period
        const endOfPeriodSunday = endOfPeriod.clone().startOf('day');
        const endOfPeriodSaturday = endOfPeriod.clone().subtract(1, 'day').startOf('day');
        const endOfPeriodFriday = endOfPeriod.clone().subtract(2, 'day').startOf('day');

        return (
            selectedDate.isSame(endOfPeriodSunday) ||
            selectedDate.isSame(endOfPeriodSaturday) ||
            selectedDate.isSame(endOfPeriodFriday)
        );
    }
    // For weekly periods, treat Friday, Saturday and Sunday as end of period
    const dayOfWeek = date.isoWeekday();
    return dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 7;
};

const filterPricingGroupsBySelectedPricingGroup = ({
    selectedPricingGroup,
    allPricingGroups,
    periodPricingGroups,
    summaryPricingGroups,
}: {
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    allPricingGroups: Array<Types.PricingGroup>;
    periodPricingGroups: null | Array<Types.PeriodPricingGroup>;
    summaryPricingGroups?: Types.SummaryPricingGroups;
}): Array<Types.PricingGroup> => {
    if (selectedPricingGroup && selectedPricingGroup === AUTOMATIC_PRICING_GROUP_ID) {
        if (!periodPricingGroups) {
            return [];
        }

        const filteredPricingGroups = allPricingGroups
            .map(pricingGroup => {
                const maybePeriodPricingGroup = periodPricingGroups.find(
                    periodPricingGroup => periodPricingGroup.pricingGroupId === pricingGroup.id,
                );
                if (hasValue(summaryPricingGroups?.[pricingGroup.id])) {
                    if (hasValue(maybePeriodPricingGroup)) {
                        return pricingGroup;
                    }
                    return {...pricingGroup, price: NaN};
                }
                if (hasValue(maybePeriodPricingGroup)) {
                    return pricingGroup;
                }
                return null;
            })
            .filter(hasValue);

        const childrenInAllPricingGroups = periodPricingGroups.flatMap(
            periodPricingGroup => periodPricingGroup.children,
        );
        const uniqueChildrenInAllPricingGroups = [...new Set(childrenInAllPricingGroups)];

        if (uniqueChildrenInAllPricingGroups.length === 1) {
            return singleChildPricingGroups(filteredPricingGroups);
        }
        return filteredPricingGroups;
    }

    return allPricingGroups.filter(pricingGroup => pricingGroup.id === selectedPricingGroup);
};

const singleChildPricingGroups = (pricingGroups: Array<Types.PricingGroup>) => {
    return pricingGroups.reduce<Array<Types.PricingGroup>>(
        produce((acc, current) => {
            if (isEmpty(acc)) {
                acc = [current];
                return acc;
            }

            const pricingGroupWithInvalidPrice = acc.find(pricingGroup => isNaN(pricingGroup.price));
            if (pricingGroupWithInvalidPrice && !isNaN(current.price)) {
                pricingGroupWithInvalidPrice.price = current.price;
            }
            return acc;
        }),
        [],
    );
};

const sortPricingGroups = <T extends Array<Types.PricingGroup | Types.SummaryPeriodItem['pricingGroups'][number]>>(
    pricingGroups: T,
    orderMapping: Types.SummaryPricingGroups,
): T =>
    pricingGroups.sort((a, b) => {
        if (!hasValue(orderMapping[a.id]) || !hasValue(orderMapping[b.id])) {
            return 0;
        }
        return orderMapping[a.id].order - orderMapping[b.id].order;
    });

const periodPricingGroups = (
    args: Parameters<typeof filterChildrenPricingGroupsByPeriod>[0],
): Array<Types.PeriodPricingGroup> => childrenByUniquePricingGroup(filterChildrenPricingGroupsByPeriod(args));

const filterChildrenPricingGroupsByPeriod = ({
    periodDateRange,
    selectedChildren,
}: {
    periodDateRange: PeriodDateRange;
    selectedChildren: Types.State['childrenState']['selectedChildren'];
}): Array<Types.Child> =>
    selectedChildren
        .map<null | Types.Child>(child => {
            const pricingGroupsInDateRange = child.pricingGroups.filter(pricingGroup => {
                if (!pricingGroup.to) {
                    return pricingGroup.from.isSameOrBefore(periodDateRange.unformattedEndDate);
                }

                return (
                    pricingGroup.from.isBetween(
                        periodDateRange.unformattedStartDate,
                        periodDateRange.unformattedEndDate,
                        'day',
                        '[]',
                    ) ||
                    pricingGroup.to.isBetween(
                        periodDateRange.unformattedStartDate,
                        periodDateRange.unformattedEndDate,
                        'day',
                        '[]',
                    ) ||
                    (pricingGroup.from.isBefore(periodDateRange.unformattedStartDate) &&
                        pricingGroup.to.isAfter(periodDateRange.unformattedEndDate))
                );
            });

            if (!isNonEmpty(pricingGroupsInDateRange)) {
                return null;
            }

            return {...child, pricingGroups: pricingGroupsInDateRange};
        })
        .filter(hasValue);

const childrenByUniquePricingGroup = (children: Array<Types.Child>): Array<Types.PeriodPricingGroup> =>
    children.reduce<Array<Types.PeriodPricingGroup>>((childAcc, child) => {
        return child.pricingGroups.reduce<Array<Types.PeriodPricingGroup>>(
            produce((pricingGroupAcc, pricingGroup) => {
                const existingPricingGroup = pricingGroupAcc.find(
                    pricingGroupAcc => pricingGroupAcc.pricingGroupId === pricingGroup.id,
                );
                if (!hasValue(existingPricingGroup)) {
                    return [...pricingGroupAcc, {pricingGroupId: pricingGroup.id, children: [child]}];
                }
                existingPricingGroup.children = [...existingPricingGroup.children, child];
                return pricingGroupAcc;
            }),
            childAcc,
        );
    }, []);

export const calculateBillingInfoByPricingGroupForStdSummaryPeriod = (
    periodPricingGroups: Types.PeriodPricingGroup[],
    period: Types.Period,
    selectedBillingFrequency: Types.BillingFrequencyOption | null,
    isEditMode: boolean,
    tuitions: Types.Tuition[],
    fees: Types.Fee[],
    discounts: Types.Discount[],
): Types.SummaryBillingInfo[] => {
    if (!isEditMode) {
        return periodPricingGroups.map(() => ({isAlreadyBilled: false, priceQuality: 'planned'}));
    }
    if (!hasValue(selectedBillingFrequency) || selectedBillingFrequency.type === 'none') {
        return periodPricingGroups.map(() => ({
            isAlreadyBilled: false,
            priceQuality: 'unknown',
            priceQualityReason: 'noBillingFrequency',
        }));
    }
    const billingFrequency = selectedBillingFrequency.type;

    const wellPricedPeriodForChildGroups = calculateWellPricedPeriodForChildGroups(
        periodPricingGroups,
        period.unformattedStartDate,
        period.unformattedEndDate,
        billingFrequency,
        tuitions,
        fees,
        discounts,
    );
    return wellPricedPeriodForChildGroups.map(wellPricedPeriodForChildGroup => {
        if (wellPricedPeriodForChildGroup.wellPriced) {
            return {
                isAlreadyBilled: false,
                priceQuality: 'planned',
            };
        }
        return {
            isAlreadyBilled: false,
            priceQuality: 'unknown',
            priceQualityReason: wellPricedPeriodForChildGroup.reason,
        };
    });
};

const calculateWellPricedPeriodForChildGroups = (
    childGroups: Pick<Types.PeriodPricingGroup, 'children'>[],
    periodStartDate: Moment,
    periodEndDate: Moment,
    billingFrequency: Types.InitialBillingFrequency,
    tuitions: Types.Tuition[],
    fees: Types.Fee[],
    discounts: Types.Discount[],
): WellPricedPeriod[] => {
    const period = {
        periodStartDate,
        periodEndDate,
        frequency: billingFrequency,
        customStartDate: null,
        customEndDate: null,
    };
    const tuitionPeriodById = getTuitionPeriod(toPartialFunction(indexBy(tuitions, tuition => tuition.id)));
    const discountPeriodById = getDiscountPeriod(toPartialFunction(indexBy(discounts, discount => discount.id)));
    const feePeriodById = getFeePeriod(toPartialFunction(indexBy(fees, fee => fee.id)));

    // TODO: I really want to refactor this. It depends less and less on the
    // child plans. However the test coverage of this function (or maybe
    // better, the functions calling this) is not good enough.
    return childGroups.map(
        (childGroup): WellPricedPeriod =>
            hasValue(childGroup.children)
                ? childGroup.children
                      .map(child =>
                          isWellPricedPeriod(
                              period,
                              child.plans.map(() =>
                                  makeWellPricablePlanWithCustomPayables(
                                      periodStartDate,
                                      periodEndDate,
                                      billingFrequency,
                                      tuitions.map(tuition => tuition.id),
                                      fees.map(fee => fee.id),
                                      discounts.map(discount => discount.id),
                                  ),
                              ),
                              tuitionPeriodById,
                              discountPeriodById,
                              feePeriodById,
                          ),
                      )
                      .reduce(mergeWellPricedPeriods, {wellPriced: true})
                : {wellPriced: true},
    );
};

type PeriodDateRange = {unformattedStartDate: Moment; unformattedEndDate: Moment | null};
const initialPeriodPricingGroup = <T extends PeriodDateRange>(
    periodDateRange: null | T,
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'],
    selectedChildren: Types.State['childrenState']['selectedChildren'],
): null | Array<Types.PeriodPricingGroup> => {
    if (!selectedPricingGroup) {
        return null;
    }

    if (selectedPricingGroup === AUTOMATIC_PRICING_GROUP_ID && hasValue(periodDateRange)) {
        return periodPricingGroups({periodDateRange, selectedChildren});
    }
    return [{pricingGroupId: selectedPricingGroup, children: selectedChildren}];
};

const sortDiscounts = (discounts: Array<Types.Discount>) => {
    return [...discounts].sort((a, b) => {
        if (a.type === 'preset' && b.type !== 'preset') {
            return -1;
        }
        if (a.type !== 'preset' && b.type === 'preset') {
            return 1;
        }
        return 0;
    });
};

const calculateDiscountAmount = (currentDiscount: Types.Discount, total: number): number => {
    return currentDiscount.type === 'custom' && currentDiscount.customDiscountType === 'amount'
        ? currentDiscount.amount
        : parseFloat((currentDiscount.amount * total).toFixed(2));
};

const calculateDiscountAmountsAndTotal = (
    discounts: Array<Types.Discount>,
    summaryPeriodTotals: Array<Types.SummaryPeriodTotal>,
) => {
    return discounts.reduce<{
        totals: Array<Types.SummaryPeriodTotal>;
        discounts: Array<Types.DiscountAmount>;
    }>(
        (accumulator, currentDiscount) => {
            const pricingGroupsWithDiscountAmountAndTotal = accumulator.totals.map(totalPricingGroup => {
                const discountAmount = calculateDiscountAmount(currentDiscount, totalPricingGroup.price);

                return {
                    amount: discountAmount,
                    id: totalPricingGroup.id,
                    total: parseFloat((totalPricingGroup.price - discountAmount).toFixed(2)),
                    children: totalPricingGroup.children,
                };
            });

            accumulator.discounts = [
                ...accumulator.discounts,
                {
                    title: currentDiscount.title,
                    pricingGroups: pricingGroupsWithDiscountAmountAndTotal.map(pricingGroup => ({
                        id: pricingGroup.id,
                        price: pricingGroup.amount,
                    })),
                },
            ];

            accumulator.totals = pricingGroupsWithDiscountAmountAndTotal.map(pricingGroup => {
                return {id: pricingGroup.id, price: pricingGroup.total, children: pricingGroup.children};
            });

            return accumulator;
        },
        {
            totals: summaryPeriodTotals,
            discounts: [],
        },
    );
};

const getDay = (day: string) => {
    const dayMap: Record<string, ApiTypes.ApiSessionPackageCreation['availability'][number]['day']> = {
        monday: 'MONDAY',
        tuesday: 'TUESDAY',
        wednesday: 'WEDNESDAY',
        thursday: 'THURSDAY',
        friday: 'FRIDAY',
        saturday: 'SATURDAY',
        sunday: 'SUNDAY',
    };

    const matchingDay = dayMap[day.toLowerCase()];
    if (!matchingDay) {
        GlobalEvent.updateError(i18next.t('somethingWentWrong'));
        throw new Error(`Could not find availability day: ${day}`);
    }
    return matchingDay;
};

const hasAllPricingGroups = <
    T extends ({type: 'tuition'} & ApiTypes.ApiSessionPackageCreation) | ({type: 'fee'} & ApiTypes.ApiProductCreation),
>(
    item: T,
    allChildren: Array<Types.Child>,
) => {
    const allPricingGroups = childrenByUniquePricingGroup(allChildren).map(({pricingGroupId}) => pricingGroupId);
    return allPricingGroups.every(pricingGroupId => {
        switch (item.type) {
            case 'tuition': {
                return item.pricing.some(pricing => pricing.pricingGroupId === pricingGroupId);
            }
            case 'fee': {
                return item.pricing.some(pricing => pricing.pricingGroupId === pricingGroupId);
            }
            default:
                return false;
        }
    });
};

const toLowerCaseWithoutWhitespaces = (str: string): string => str.replace(/\s/g, '').toLowerCase();

/** True iff `planId` indicates the chronological last plan in `plans`. */
const isChronologicalLastPlan = (plans: Array<Types.Plan>, planId: string): boolean => {
    const selectedPlan = plans.find(plan => plan.id === planId);
    return (
        hasValue(selectedPlan) &&
        plans.every(plan => plan.id === selectedPlan.id || plan.startDate.isBefore(selectedPlan.startDate))
    );
};

const getLastDay = (selectedChildren: Array<Types.Child>): Moment | null => {
    if (!isNonEmpty(selectedChildren)) {
        return null;
    }

    // We do not take last day into considaration for multiple children in create view
    if (selectedChildren.length > 1) {
        return null;
    }

    if (!hasValue(head(selectedChildren).lastDay)) {
        return null;
    }

    return moment(head(selectedChildren).lastDay);
};

const isFirstDayOfBillingPeriod = (
    startDate: Moment,
    billingFrequencyType: Types.BillingFrequencyOption['type'],
    biweeklyBillingPeriodStartDate: Moment,
): boolean => {
    switch (billingFrequencyType) {
        case 'monthly':
            return startDate.date() === 1;
        case 'weekly':
            return startDate.isoWeekday() === 1;
        case 'biweekly': {
            const closestBiweeklyBillingPeriodStartDate = getClosestBiweeklyStartDate(
                biweeklyBillingPeriodStartDate,
                startDate,
            );
            return startDate.isSame(closestBiweeklyBillingPeriodStartDate, 'day');
        }
        default:
            return false;
    }
};

const pickBillingFrequencyOfChildBillPayer = (child: Types.Child): Types.InitialBillingFrequency | null => {
    if (isEmpty(child.billPayers)) {
        return null;
    }
    // "Randomly" pick the first bill payer
    return child.billPayers[0].billingFrequency ?? null;
};

export {
    allSelectedChildrenHaveBillPayer,
    allSelectedTuitionsHaveTheSameFrequency,
    calculatePricingGroupTotals,
    isAutomaticPricingGroup,
    lowestAndHighestPrice,
    pricingGroupSelectPlacement,
    selectedChildren,
    selectedPricingGroup,
    isEndOfPeriodDate,
    filterPricingGroupsBySelectedPricingGroup,
    periodPricingGroups,
    sortPricingGroups,
    childrenByUniquePricingGroup,
    filterChildrenPricingGroupsByPeriod,
    initialPeriodPricingGroup,
    singleChildPricingGroups,
    sortDiscounts,
    calculateDiscountAmount,
    calculateDiscountAmountsAndTotal,
    getDay,
    hasAllPricingGroups,
    toLowerCaseWithoutWhitespaces,
    isChronologicalLastPlan,
    getLastDay,
    isFirstDayOfBillingPeriod,
    pickBillingFrequencyOfChildBillPayer,
};
